/**
 * AuthContext — gestion de la session utilisateur (social login).
 * Le token JWT est stocké dans localStorage.
 * Aucun mot de passe n'est géré ici (social only).
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'auth0expert_session_v1';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]   = useState(null);   // { id, email, name, picture, provider }
  const [token, setToken] = useState(null);   // JWT

  // Restaurer depuis localStorage au démarrage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const { user: u, token: t } = JSON.parse(raw);
        // Vérification expiration côté client (lecture du payload JWT)
        if (t && u) {
          const [, payload] = t.split('.');
          const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
          if (claims.exp * 1000 > Date.now()) {
            setUser(u);
            setToken(t);
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const login = useCallback((userData, jwtToken) => {
    setUser(userData);
    setToken(jwtToken);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ user: userData, token: jwtToken }));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé à l\'intérieur de <AuthProvider>');
  return ctx;
}
